import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Colors,
  Icons,
  Skeleton,
  Stack,
  UI,
  classNames,
  createClasses,
  pxToRem,
  spacing,
} from '@kp/react-ui';

import urlJoin from 'url-join';

import { OpenAppButton } from '@/components/OpenAppButton';
import type { DashboardPeriod } from '@/constants/dashboard-period';

import { INSIGHTS_PATHS } from '../../constants';
import type { AppAccess, SpaceScope } from '../../types/home-types';
import { AppHeading } from '../AppHeading';
import { LearnMoreButton } from '../LearnMoreButton';

import { AlarmsFindingsCardContainer } from './AlarmsFindingsCardContainer';
import { EnergyConsumptionCardContainer } from './EnergyConsumptionCardContainer';
import { IndoorClimateCardContainer } from './IndoorClimateCardContainer';
import { InstallationsCardContainer } from './InstallationsCardContainer';
import { KPI_CARD_MIN_HEIGHT } from './KpiCard';

// AppHeading: the 24px title row, an md gap and one 16px description line
const HEADING_HEIGHT = pxToRem(56);

const classes = createClasses({
  root: {
    padding: spacing('lg'),
    borderRadius: UI.CONTAINER_RADIUS,
    border: `1px solid ${Colors.Neutral.borderMedium}`,
    boxShadow: UI.CONTAINER_SHADOW,
    backgroundColor: Colors.Neutral.background,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: spacing('md'),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing('xs'),
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('lg'),
    height: 'auto',
    padding: spacing('lg'),
    borderRadius: UI.CONTAINER_RADIUS,
  },
  skeletonHeading: {
    height: HEADING_HEIGHT,
  },
  skeletonKpi: {
    minHeight: KPI_CARD_MIN_HEIGHT,
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: '3fr 3fr 3fr 4fr',
    gap: spacing('md'),
    '@media (max-width: 1280px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 720px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  alarmsCard: {
    '@media (max-width: 1280px)': {
      gridColumn: '1 / -1',
    },
  },
});

export interface InsightsAppCardProps extends AppAccess {
  scope?: SpaceScope;
  timeSpan: DashboardPeriod;
}

/**
 * The Insights card. While its availability loads, a skeleton laid out like
 * the enabled card, so it keeps its size. Enabled, it opens Insights at the
 * URL the backend tells and shows the KPIs, each deep-linking into it.
 * Disabled, it is only the text and a "Learn more" button at the bottom left
 * and a lock icon, like the other app cards.
 */
export const InsightsAppCard: React.FC<InsightsAppCardProps> = ({
  url,
  disabled,
  loading,
  scope,
  timeSpan,
}) => {
  const { t } = useTranslation();
  const title = t('home.insights.title');
  const enabled = !disabled && !!url;

  const linkTo = (path: string) => (url ? urlJoin(url, path) : undefined);

  if (loading) {
    // One shimmering block; its transparent children only give it the size
    return (
      <Skeleton
        className={classes.skeleton}
        aria-busy="true"
        data-testid="app-card-skeleton"
      >
        <div className={classes.skeletonHeading} />
        <div className={classes.kpiGrid}>
          <div className={classes.skeletonKpi} />
          <div className={classes.skeletonKpi} />
          <div className={classes.skeletonKpi} />
          <div
            className={classNames(classes.skeletonKpi, classes.alarmsCard)}
          />
        </div>
      </Skeleton>
    );
  }

  const heading = (
    <AppHeading
      icon={Icons.IconDashboard}
      iconColor={Colors.Selected.icon}
      title={title}
      description={t('products.insights.description')}
      locked={!enabled}
    />
  );

  if (!enabled) {
    // Like the other app cards: the text, then Learn more at the bottom left
    return (
      <Stack gap="lg" className={classes.root}>
        {heading}
        <div className={classes.actions}>
          <LearnMoreButton appName={title} />
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.root}>
      <div className={classes.header}>
        {heading}
        <OpenAppButton variant="primary" label={title} url={url} />
      </div>
      <div className={classes.kpiGrid}>
        <EnergyConsumptionCardContainer
          scope={scope}
          timeSpan={timeSpan}
          href={linkTo(INSIGHTS_PATHS.energy)}
        />
        <IndoorClimateCardContainer
          scope={scope}
          timeSpan={timeSpan}
          href={linkTo(INSIGHTS_PATHS.indoorClimate)}
        />
        <InstallationsCardContainer
          scope={scope}
          timeSpan={timeSpan}
          href={linkTo(INSIGHTS_PATHS.installations)}
        />
        <AlarmsFindingsCardContainer
          scope={scope}
          href={linkTo(INSIGHTS_PATHS.alarms)}
          className={classes.alarmsCard}
        />
      </div>
    </Stack>
  );
};
